import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Copy, RefreshCw, Apple, ExternalLink } from "lucide-react";

/**
 * Personal calendar subscription card. Renders inside Settings.
 *
 * The .ics feed is served un-authenticated at /api/calendar/ical/{token}.ics
 * because Apple / Google / Outlook cannot send bearer tokens when polling a
 * subscribed URL. Rotating the token invalidates the old link — that's the
 * only way to "log out" of a specific device.
 */
export default function CalendarSync() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Strip protocol → webcal://host/path (Apple + Outlook one-click subscribe)
  const httpsUrl = meta?.path ? `${base}${meta.path}` : "";
  const webcalUrl = httpsUrl.replace(/^https?:\/\//, "webcal://");
  const googleUrl = httpsUrl
    ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(httpsUrl)}`
    : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/me/ical");
      setMeta(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load calendar link");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — please long-press the URL to copy manually");
    }
  };

  const rotate = async () => {
    if (!confirm("This immediately unsubscribes every device using the old link. Continue?")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/calendar/me/ical/rotate");
      setMeta(data);
      toast.success("New link issued — resubscribe on your devices");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rotate the link");
    } finally {
      setRotating(false);
    }
  };

  return (
    <section
      data-testid="settings-calendar-sync-section"
      className="rounded-lg border border-white/10 bg-[#121212] p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold">
          <Calendar className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display text-xl leading-tight">Sync to your personal calendar</div>
          <div className="text-xs text-zinc-500">
            Subscribe once and every call, update, or cancellation lands on your phone alongside real life.
          </div>
        </div>
      </div>

      {loading || !meta ? (
        <div className="text-xs text-zinc-500 py-6 text-center">Loading your link…</div>
      ) : (
        <div className="mt-5 space-y-5">
          {/* URL block */}
          <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-3 flex items-center gap-2">
            <code
              data-testid="calendar-sync-url"
              className="text-[11px] md:text-xs text-zinc-300 flex-1 truncate font-mono"
              title={webcalUrl}
            >
              {webcalUrl}
            </code>
            <button
              data-testid="calendar-sync-copy-btn"
              onClick={() => copy(webcalUrl, "Subscribe URL")}
              className="h-8 px-3 rounded-md border border-white/10 text-xs text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>

          {/* One-click subscribe buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <a
              data-testid="calendar-sync-apple-btn"
              href={webcalUrl}
              className="h-10 rounded-md border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2"
            >
              <Apple className="w-4 h-4" /> Add to Apple Calendar
            </a>
            <a
              data-testid="calendar-sync-google-btn"
              href={googleUrl}
              target="_blank"
              rel="noreferrer"
              className="h-10 rounded-md border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Add to Google Calendar
            </a>
            <a
              data-testid="calendar-sync-outlook-btn"
              href={webcalUrl}
              className="h-10 rounded-md border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Add to Outlook
            </a>
          </div>

          {/* How-to */}
          <details className="text-xs text-zinc-400" data-testid="calendar-sync-help">
            <summary className="cursor-pointer text-zinc-300 hover:text-white">
              Show setup instructions
            </summary>
            <div className="mt-3 space-y-3 leading-relaxed">
              <div>
                <div className="text-zinc-200 font-medium">Apple Calendar (iPhone / iPad)</div>
                <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                  <li>Tap <b>Add to Apple Calendar</b> above.</li>
                  <li>Choose an account and tap <b>Subscribe</b>.</li>
                </ol>
              </div>
              <div>
                <div className="text-zinc-200 font-medium">Google Calendar</div>
                <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                  <li>Click <b>Add to Google Calendar</b>. It opens Google&apos;s subscribe screen.</li>
                  <li>Confirm — the calendar appears in the left sidebar within a minute.</li>
                </ol>
              </div>
              <div>
                <div className="text-zinc-200 font-medium">Outlook (desktop or web)</div>
                <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                  <li>Copy the URL above.</li>
                  <li>In Outlook: <i>Add calendar → Subscribe from web</i>, paste the link, name it &quot;StageCall&quot;.</li>
                </ol>
              </div>
              <div className="text-[11px] text-zinc-500 pt-2">
                Calendar apps refresh every 1–24 hours. If a change urgently needs to appear, ask your calendar app to refresh manually.
              </div>
            </div>
          </details>

          {/* Rotate */}
          <div className="border-t border-white/5 pt-4 flex items-center justify-between gap-4">
            <div className="text-[11px] text-zinc-500 max-w-md">
              Rotating your link immediately unsubscribes every device using the old one. Use it if a device is lost or the link was shared by mistake.
            </div>
            <button
              data-testid="calendar-sync-rotate-btn"
              onClick={rotate}
              disabled={rotating}
              className="h-9 px-3 rounded-md border border-white/10 text-xs text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} />
              {rotating ? "Rotating…" : "Rotate link"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
